export type {
  PatchPilotConfig,
  PatchPilotOffset,
  ResolvedConfig,
} from "./core/config";
export { DEFAULT_CONFIG, resolveConfig } from "./core/config";
export { ApiClient, ApiError } from "./core/api-client";
export type {
  CreateTicketPayload,
  TicketResponse,
  ScreenshotResponse,
  CategoryResponse,
} from "./core/api-client";
export { SdkState, sdkState } from "./core/state";
export type { SdkEventMap, SdkEventName } from "./core/state";

import {
  type PatchPilotConfig,
  type PatchPilotOffset,
  resolveConfig,
} from "./core/config";
import { ApiClient } from "./core/api-client";
import { sdkState } from "./core/state";
import {
  createContainer,
  destroyContainer,
  setContainerTheme,
} from "./ui/container";
import {
  createFloatingButton,
  updateFloatingButtonPosition,
  clearFloatingButton,
} from "./ui/floating-button";
import { createReportForm, destroyReportForm } from "./ui/report-form";

// ---------------------------------------------------------------------------
// Internal references for cleanup
// ---------------------------------------------------------------------------

let apiClient: ApiClient | null = null;
let _floatingButton: HTMLButtonElement | null = null;
let _reportForm: HTMLElement | null = null;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const PatchPilot = {
  /**
   * Initialize the PatchPilot SDK.
   * Creates the floating button and report form inside a Shadow DOM container.
   */
  init(config: PatchPilotConfig): void {
    // Validate required fields
    if (!config.apiKey) {
      throw new Error("PatchPilot: apiKey is required");
    }
    if (!config.apiUrl) {
      throw new Error("PatchPilot: apiUrl is required");
    }

    // Clean up any previous instance
    PatchPilot.destroy();

    // Resolve config with defaults
    const resolved = resolveConfig(config);

    // Create API client
    apiClient = new ApiClient(resolved.apiUrl, resolved.apiKey);

    // Create Shadow DOM container
    const shadowRoot = createContainer();
    setContainerTheme(resolved.theme);

    // Create UI elements
    _floatingButton = createFloatingButton(shadowRoot, resolved);
    _reportForm = createReportForm(shadowRoot, apiClient, resolved);

    // Store config in state
    sdkState.config = resolved;
  },

  /**
   * Dynamically update the floating button position and offset
   * without full SDK re-initialization.
   */
  setPosition(
    position: "bottom-right" | "bottom-left",
    offset?: PatchPilotOffset,
  ): void {
    updateFloatingButtonPosition(position, offset);
    if (sdkState.config) {
      sdkState.config.position = position;
      if (offset) sdkState.config.offset = offset;
    }
  },

  /**
   * Destroy the SDK instance and clean up all resources.
   */
  destroy(): void {
    destroyReportForm();
    clearFloatingButton();
    destroyContainer();
    sdkState.reset();
    apiClient = null;
    _floatingButton = null;
    _reportForm = null;
  },
};

// Expose to window for IIFE usage
if (typeof window !== "undefined") {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).PatchPilot = PatchPilot;
}
